/**
 * PKCE helpers (RFC 7636) for the Hosted UI authorization-code flow.
 *
 * The app client is public -- a browser cannot keep a secret -- so PKCE is what
 * stops an intercepted authorization code from being redeemed by anyone else:
 * the code is only exchangeable together with the verifier, and the verifier
 * never leaves this tab.
 *
 * WebCrypto needs a secure context. https qualifies, and so does
 * http://localhost, which is why local development works without TLS.
 */

const webCrypto = () => {
    const c = typeof window !== 'undefined' ? window.crypto : undefined;
    if (!c || !c.subtle || typeof c.getRandomValues !== 'function') {
        throw new Error(
            'Secure sign-in needs WebCrypto. Open the portal over https, or on http://localhost.',
        );
    }
    return c;
};

/** RFC 4648 section 5: base64 with a URL-safe alphabet and no padding. */
export const base64UrlEncode = (bytes) => {
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    let binary = '';
    for (let i = 0; i < view.length; i += 1) {
        binary += String.fromCharCode(view[i]);
    }
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * A random URL-safe string. 32 bytes gives a 43-character PKCE verifier, the
 * minimum RFC 7636 allows, with 256 bits of entropy.
 */
export const randomUrlSafe = (byteLength = 32) => {
    const bytes = new Uint8Array(byteLength);
    webCrypto().getRandomValues(bytes);
    return base64UrlEncode(bytes);
};

/** code_challenge = BASE64URL(SHA-256(ASCII(code_verifier))). */
export const codeChallengeS256 = async (verifier) => {
    const digest = await webCrypto().subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64UrlEncode(new Uint8Array(digest));
};
